import React, { createContext, useContext, useMemo } from 'react';
import {
  useStudioVoiceBranding,
  useThemeColors,
  type StudioVoiceBranding,
} from '@brandclub/bc-widgets';
import './voice-tokens.css';

// ─── Context ─────────────────────────────────────────────────────────────────

interface VoiceThemeCtx {
  persona: StudioVoiceBranding['persona'];
  visual: StudioVoiceBranding['visual'];
}

const VoiceThemeContext = createContext<VoiceThemeCtx>({
  persona: { assistantName: 'Benny' },
  visual: {},
});

export const useVoiceThemeContext = (): VoiceThemeCtx => useContext(VoiceThemeContext);

// ─── CSS custom property builder ─────────────────────────────────────────────

/**
 * D-61: per-voice color UI was removed. The voice surface now derives ONLY
 * widget colors from the unified Identity tab (`textPrimary` → widget fg,
 * `primary` → widget CTA, `buttonText` → CTA label). All other CSS vars
 * (`--voice-stage-bg-*`, `--voice-mute-*`, `--voice-end-*`, bubble colors,
 * status pill dots) fall through to the `voice-tokens.css` defaults.
 */
function buildCssVars(brandColors: Record<string, string>): React.CSSProperties {
  const vars: Record<string, string> = {};

  if (brandColors.textPrimary) {
    vars['--voice-widget-fg'] = brandColors.textPrimary;
    vars['--voice-widget-price'] = brandColors.textPrimary;
  }
  if (brandColors.primary) {
    vars['--voice-widget-button-bg'] = brandColors.primary;
    vars['--voice-widget-accent'] = brandColors.primary;
    vars['--voice-widget-accent-dark'] = brandColors.primary;
  }
  if (brandColors.buttonText) {
    vars['--voice-widget-button-fg'] = brandColors.buttonText;
  }

  return vars as React.CSSProperties;
}

// ─── Provider ────────────────────────────────────────────────────────────────

export interface VoiceThemeProviderProps {
  children: React.ReactNode;
  /**
   * Optional in-memory draft overrides. When provided, these take priority
   * over the persisted studio config — used by previews to show live edits
   * before saving. Currently only `visual.blobStyle` is meaningful since
   * D-61 removed per-voice persona/copy/color overrides.
   */
  overrides?: Partial<StudioVoiceBranding>;
}

/**
 * Wraps the voice call surface. Sets brand-identity-driven `--voice-widget-*`
 * CSS custom properties on its root div so all descendant components can use
 * them without prop drilling.
 *
 * NOTE: variables are scoped to this div, not `:root`. Components rendered
 * in portals outside this div (StudioDropdown, MUI modals) won't inherit
 * them — that's intentional; portals don't carry voice branding.
 */
export const VoiceThemeProvider: React.FC<VoiceThemeProviderProps> = ({
  children,
  overrides,
}) => {
  const persisted = useStudioVoiceBranding();
  const { themeColors } = useThemeColors();

  const merged: StudioVoiceBranding = useMemo(() => {
    if (!overrides) return persisted;
    return {
      persona: { ...persisted.persona, ...overrides.persona },
      visual: {
        blobStyle: overrides.visual?.blobStyle ?? persisted.visual?.blobStyle,
        blobEnabled: overrides.visual?.blobEnabled ?? persisted.visual?.blobEnabled,
        blobColors: overrides.visual?.blobColors ?? persisted.visual?.blobColors,
      },
    };
  }, [persisted, overrides]);

  const cssVars = useMemo(() => buildCssVars(themeColors), [themeColors]);

  const ctxValue = useMemo<VoiceThemeCtx>(
    () => ({ persona: merged.persona, visual: merged.visual ?? {} }),
    [merged.persona, merged.visual],
  );

  return (
    <VoiceThemeContext.Provider value={ctxValue}>
      <div style={{ display: 'contents', ...cssVars }}>{children}</div>
    </VoiceThemeContext.Provider>
  );
};

export default VoiceThemeProvider;
